<template>
  <div class="task-observers-wrapper d-inline-flex align-center">
    <!-- Popover со списком наблюдателей (открывается при наведении) -->
    <VMenu
      open-on-hover
      :close-on-content-click="false"
      close-delay="200"
      location="bottom start"
      offset="8"
      content-class="date-picker-menu"
    >
      <template #activator="{ props: menuProps }">
        <div
          v-bind="menuProps"
          class="d-flex align-center cursor-pointer select-none py-1 px-1 rounded hover-bg-subtle"
        >
          <!-- Наслоение круглых аватаров -->
          <div class="observers-avatar-stack d-flex align-center mr-2">
            <VAvatar
              v-for="(item, idx) in visibleObservers"
              :key="item.id"
              size="28"
              class="observer-avatar border-surface"
              :image="getObserverAvatar(item.observer)"
              :style="{ zIndex: visibleObservers.length - idx }"
            />

            <!-- Счетчик если больше 3 -->
            <VAvatar
              v-if="remainingCount > 0"
              size="28"
              color="primary"
              variant="tonal"
              class="observer-avatar border-surface"
              style="z-index: 0"
            >
              <span class="text-caption font-weight-bold">+{{ remainingCount }}</span>
            </VAvatar>
          </div>

          <!-- Подпись кол-ва сотрудников -->
          <span class="text-body-2 text-medium-emphasis">
            {{ observersCountText }}
          </span>
        </div>
      </template>

      <!-- Выпадающая карточка списка через проектный CustomSelectedItems -->
      <CustomSelectedItems
        :items="selectedItemsList"
        :readonly="!canManage"
        @remove="handleRemoveClick"
        @remove-all="handleRemoveAllClick"
      />
    </VMenu>

    <!-- Диалог подтверждения удаления -->
    <ConfirmDialog
      :is-dialog-visible="isConfirmDialogOpen"
      :confirmation-question="confirmDialogQuestion"
      confirm-title="Удалить наблюдателя"
      confirm-btn="Удалить"
      reject-btn="Отмена"
      @confirm="handleConfirmRemove"
      @update:is-dialog-visible="isConfirmDialogOpen = $event"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import CustomSelectedItems from '@/components/createdUi/CustomSelectedItems.vue'
import { useTasksStore } from '@/modules/tasks/stores/tasksStore'
import type { ITaskObserver } from '@/modules/tasks/types/ITaskObserver'
import { getFullName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'

interface Props {
  entryId: number
  observers: ITaskObserver[]
  canManage?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canManage: true,
})

const emit = defineEmits<{
  (e: 'updated', observers: ITaskObserver[]): void
}>()

const tasksStore = useTasksStore()

const fallbackAvatars = new Map<string | number, string>()

function getObserverAvatar(observer: any): string {
  if (observer?.avatar) return observer.avatar
  const id = observer?.id || 'default'
  if (!fallbackAvatars.has(id)) {
    fallbackAvatars.set(id, getRandomAvatar())
  }
  return fallbackAvatars.get(id)!
}

// Диалог подтверждения
const isConfirmDialogOpen = ref(false)
const observerToRemove = ref<ITaskObserver | null>(null)
const isRemoveAllMode = ref(false)

const visibleObservers = computed(() => {
  return props.observers.slice(0, 3)
})

const remainingCount = computed(() => {
  return Math.max(0, props.observers.length - 3)
})

const observersCountText = computed(() => {
  const count = props.observers.length
  if (count === 0) return '0 сотрудников'
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) {
    return `${count} сотрудник`
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return `${count} сотрудника`
  }
  return `${count} сотрудников`
})

const selectedItemsList = computed(() => {
  return props.observers.map(o => ({
    id: o.observer?.id || o.id,
    name: getObserverFullName(o.observer),
    avatar: getObserverAvatar(o.observer),
    description: o.observer?.position_name || o.observer?.department_name || '',
  }))
})

const confirmDialogQuestion = computed(() => {
  if (isRemoveAllMode.value) {
    return 'Удалить всех наблюдателей из задачи?'
  }
  if (!observerToRemove.value) return 'Исключить наблюдателя?'
  const name = getObserverFullName(observerToRemove.value.observer)
  return `Исключить ${name} из наблюдателей задачи?`
})

function getObserverFullName(obs: any): string {
  if (!obs) return 'Сотрудник'
  return getFullName(obs) || obs.full_name || 'Сотрудник'
}

function handleRemoveClick(id: string | number) {
  const found = props.observers.find(o => (o.observer?.id || o.id) === id)
  if (found) {
    observerToRemove.value = found
    isRemoveAllMode.value = false
    isConfirmDialogOpen.value = true
  }
}

function handleRemoveAllClick() {
  if (props.observers.length === 0) return
  isRemoveAllMode.value = true
  observerToRemove.value = null
  isConfirmDialogOpen.value = true
}

async function handleConfirmRemove(confirmed: boolean) {
  if (confirmed) {
    if (isRemoveAllMode.value) {
      const allIds = props.observers.map(o => o.observer?.id).filter(Boolean) as string[]
      if (allIds.length > 0) {
        const updated = await tasksStore.removeObservers(props.entryId, allIds)
        emit('updated', updated)
      }
    } else if (observerToRemove.value) {
      const empId = observerToRemove.value.observer?.id
      if (empId) {
        const updated = await tasksStore.removeObservers(props.entryId, [empId])
        emit('updated', updated)
      }
    }
  }
  isConfirmDialogOpen.value = false
  observerToRemove.value = null
  isRemoveAllMode.value = false
}
</script>

<style scoped>
.observers-avatar-stack {
  position: relative;
}

.observer-avatar {
  border: 2px solid rgb(var(--v-theme-surface)) !important;
  border-radius: 50% !important;
  margin-left: -8px;
  flex-shrink: 0;
}

.observer-avatar:first-child {
  margin-left: 0;
}

.hover-bg-subtle:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}
</style>
